/* SellerProfileDrawer — slide-in panel showing the public seller profile.
 *
 * Lives at the buyer-portal scope and is opened via useSellerProfileDrawer().open(slug).
 * Free close paths from Radix Sheet:
 *   • click the dimmed backdrop
 *   • press Esc
 *   • click the built-in X button
 * The buyer's underlying page state (scroll, expanded rows, in-progress PO)
 * is preserved — the drawer is purely overlay.
 *
 * Profile data is fetched on every open from /api/public/seller/:slug.
 * The "Open full page" link in the header links to /s/:slug (new tab) so
 * sellers can still copy a shareable URL.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetCloseX } from "./ui/sheet";
import api from "../lib/api";
import {
  ShieldCheck, MapPin, Calendar, CheckCircle2, Globe, Mail, Maximize2,
  Loader2, Package, Clock, CreditCard, RotateCcw, AlertTriangle, Truck,
  Sparkles,
} from "lucide-react";

export default function SellerProfileDrawer({ slug, onOpenChange }) {
  const open = !!slug;
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);

  // Fetch on every open; clear on close.
  useEffect(() => {
    if (!slug) { setData(null); setError(null); return; }
    let cancelled = false;
    setData(null); setError(null);
    api.get(`/public/seller/${slug}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.detail || "Profile not found"); });
    return () => { cancelled = true; };
  }, [slug]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="p-0 w-full sm:max-w-[520px] flex flex-col"
        data-testid="seller-profile-drawer"
      >
        <SheetCloseX/>
        {/* Sticky header — close X is now provided by <SheetCloseX/> above. */}
        <div className="px-5 py-3.5 border-b border-border flex items-center gap-2.5 pr-12 shrink-0">
          {data?.branding?.logo_url ? (
            <img src={data.branding.logo_url} alt={data.name} className="w-7 h-7 rounded object-contain"/>
          ) : (
            <div className="w-7 h-7 rounded grid place-items-center font-bold display text-sm"
                 style={{ background: data?.branding?.primary_color || "#0a1f1c", color: data?.branding?.accent_color || "#e8ff3a" }}>
              {(data?.name || slug || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="font-medium text-[14px] flex-1 truncate" data-testid="drawer-title">
            {data?.name || (error ? "Profile not available" : "Loading…")}
          </div>
          {slug && (
            <Link
              to={`/s/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="drawer-open-full"
              className="text-[11.5px] text-foreground/55 hover:text-foreground inline-flex items-center gap-1"
              title="Open the full storefront webpage in a new tab"
            >
              <Maximize2 className="w-3 h-3"/> Open storefront
            </Link>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {!data && !error && (
            <div className="h-full grid place-items-center">
              <Loader2 className="w-5 h-5 animate-spin text-foreground/40"/>
            </div>
          )}
          {error && (
            <div className="p-8 text-center text-foreground/65 text-[13px]">{error}</div>
          )}
          {data && <DrawerBody data={data}/>}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ── body ─────────────────────────────────────────────────────────── */
function DrawerBody({ data }) {
  const p = data.profile || {};
  const pol = data.policies || {};
  const trust = data.trust || {};
  const qi = pol.qi_window_days ?? 7;
  const lead = pol.default_lead_days;
  const terms = (pol.default_terms || "").replace(/^NET_/, "Net-");

  return (
    <div className="p-5 space-y-6">
      {/* Identity */}
      <div>
        <div className="display text-2xl tracking-tight" data-testid="drawer-name">{data.name}</div>
        {p.tagline && <div className="text-[13.5px] text-foreground/70 mt-1">{p.tagline}</div>}

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {trust.kyb_verified      && <Chip tone="ok"><ShieldCheck className="w-3 h-3"/> Verified business</Chip>}
          {trust.insurance_on_file && <Chip><CheckCircle2 className="w-3 h-3"/> Insurance on file</Chip>}
          {p.location              && <Chip><MapPin className="w-3 h-3"/>{p.location}</Chip>}
          {p.founded_year          && <Chip><Calendar className="w-3 h-3"/>Founded {p.founded_year}</Chip>}
        </div>
      </div>

      {/* About */}
      {p.about && (
        <Section eyebrow="About">
          {String(p.about).split(/\n\s*\n/).filter(Boolean).map((para, i) => (
            <p key={i} className={`text-[13.5px] leading-relaxed ${i === 0 ? "text-foreground/85" : "text-foreground/65 mt-2"}`}>{para}</p>
          ))}
        </Section>
      )}

      {/* Policies */}
      <Section eyebrow="Policies & inspection terms">
        <div className="grid grid-cols-2 gap-2 mt-2">
          <PolicyCard Icon={Package}     label="Inspection window" value={`${qi} day${qi === 1 ? "" : "s"}`}/>
          {lead != null  && <PolicyCard Icon={Clock}       label="Lead time"          value={`${lead} day${lead === 1 ? "" : "s"}`}/>}
          {terms         && <PolicyCard Icon={CreditCard}  label="Payment terms"      value={terms}/>}
          <PolicyCard Icon={RotateCcw}    label="Returns"            value="Per MSA"/>
          <PolicyCard Icon={AlertTriangle}label="Damaged orders"     value="Replacement / credit"/>
          <PolicyCard Icon={Truck}        label="Shipping"           value="Continental US"/>
        </div>
        <div className="text-[11.5px] text-foreground/55 mt-2 leading-relaxed">
          Defaults shown — your bilateral agreement (MSA) may override individual lines.
        </div>
        {pol.terms_and_conditions && (
          <div className="mt-3 rounded-xl border border-border bg-white p-3" data-testid="drawer-custom-terms">
            <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/55 mb-1.5">Additional T&amp;C</div>
            <pre className="whitespace-pre-wrap font-sans text-[12.5px] leading-relaxed text-foreground/85">{pol.terms_and_conditions}</pre>
            <div className="text-[10.5px] text-foreground/50 mt-2">
              Stamped on every invoice and pack list.
            </div>
          </div>
        )}
      </Section>

      {/* Categories */}
      {Array.isArray(p.categories) && p.categories.length > 0 && (
        <Section eyebrow="What we sell">
          <div className="flex flex-wrap gap-1.5 mt-2">
            {p.categories.map(c => (
              <span key={c} className="chip rounded-full border border-border bg-secondary/40 px-2.5 h-7 inline-flex items-center text-[12px] text-foreground/80">{c}</span>
            ))}
          </div>
        </Section>
      )}

      {/* Trust signals */}
      {(trust.months_on_platform != null || trust.orders_fulfilled != null || trust.kyb_verified || trust.bank_verified || trust.insurance_on_file) && (
        <Section eyebrow="Trust signals">
          <div className="grid grid-cols-2 gap-3 mt-2">
            {trust.months_on_platform != null && <Stat l="On platform"      v={`${trust.months_on_platform} mo`}/>}
            {trust.orders_fulfilled  != null && <Stat l="Orders fulfilled" v={trust.orders_fulfilled.toLocaleString()}/>}
          </div>
          <div className="mt-3 space-y-1.5">
            {trust.kyb_verified      && <CheckRow>EIN verified · Middesk</CheckRow>}
            {trust.bank_verified     && <CheckRow>Bank account verified · Plaid</CheckRow>}
            {trust.insurance_on_file && <CheckRow>Insurance on file · Allianz Trade</CheckRow>}
          </div>
        </Section>
      )}

      {/* Contact */}
      {(p.contact_email || p.website) && (
        <Section eyebrow="Contact">
          <div className="space-y-1.5 mt-2 text-[13px]">
            {p.contact_email && <div className="flex items-center gap-2 text-foreground/80"><Mail className="w-3.5 h-3.5"/>{p.contact_email}</div>}
            {p.website       && <div className="flex items-center gap-2 text-foreground/80"><Globe className="w-3.5 h-3.5"/>{p.website.replace(/^https?:\/\//, "")}</div>}
          </div>
        </Section>
      )}
    </div>
  );
}

/* ── atoms ───────────────────────────────────────────────────────── */
function Section({ eyebrow, children }) {
  return (
    <section>
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-accent"/>{eyebrow}
      </div>
      <div className="mt-2">{children}</div>
    </section>
  );
}
function PolicyCard({ Icon, label, value }) {
  return (
    <div className="rounded-lg border border-border bg-white p-3">
      <div className="flex items-center gap-1.5 text-foreground/55">
        <Icon className="w-3.5 h-3.5"/>
        <span className="text-[10.5px] mono uppercase tracking-[0.12em]">{label}</span>
      </div>
      <div className="text-[14px] mt-1.5 leading-tight">{value}</div>
    </div>
  );
}
function Stat({ l, v }) {
  return (
    <div className="rounded-lg bg-secondary/40 px-3 py-2.5">
      <div className="display text-lg leading-none">{v}</div>
      <div className="text-[10.5px] mono uppercase tracking-[0.12em] text-foreground/55 mt-1">{l}</div>
    </div>
  );
}
function CheckRow({ children }) {
  return (
    <div className="flex items-center gap-2 text-[12.5px] text-foreground/75">
      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0"/>{children}
    </div>
  );
}
function Chip({ children, tone = "muted" }) {
  const cls = tone === "ok"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-secondary text-foreground/75 border-border";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 h-6 text-[11px] mono uppercase tracking-[0.1em] ${cls}`}>
      {children}
    </span>
  );
}
